import { NoddiIcon, NoddiIconName, cn, cva } from 'noddi-ui-common';
import { commonTexts, copySessionQueryParams, toSentenceCase } from 'noddi-util';
import { ReactNode, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../base/dropdown-menu';
import { selectionControlItemVariants, selectionControlListVariants } from '../../base/selectionControlVariants';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../base/tabs';
import { useSessionQueryParams } from '../../hooks/sessionQueryParamContext';
import { NoddiBadge } from '../Other/NoddiBadge';
import { useOverflowVisibleCount } from './useOverflowVisibleCount';

/**
 * @interface TabItem
 * @property {string} label is the text that will be displayed in the tab
 * @property {string} value is the value that will be used in the url
 * @property {ReactNode} node is the content that will be displayed when the tab is active
 * @property {IconName} icon is the optional icon that will be displayed alongside the label (supports both Lucide and Custom icons)
 * @property {number} count is the optional count to be displayed as a badge next to the label
 * @property {ReactNode} badge is the optional custom badge element to be displayed next to the label
 * @property {boolean} hide is the optional property to hide the tab from being displayed
 * @property {boolean} disabled is the optional property to disable the tab
 */
export interface TabItem<T extends string> {
  label: string;
  value: T;
  node: ReactNode;
  searchParamName?: string;
  icon?: NoddiIconName;
  count?: number;
  badge?: ReactNode;
  hide?: boolean;
  disabled?: boolean;
}

export interface NoddiTabsTranslations {
  more: string;
}

interface NoddiTabsProps<T extends string> {
  tabItems: TabItem<T>[];
  defaultValue: T;
  ariaLabel?: string;
  searchParamName?: string;
  value?: T;
  onValueChange?: (value: T) => void;
  keepExistingSearchParams?: boolean;
  trailingNode?: ReactNode;
  contentClassName?: string;
  /**
   * Stretches the tabs to fill the remaining height of their parent and makes each tab panel a
   * `min-h-0` flex column, so a `fillHeight` child (e.g. `NoddiTable`) inside a tab fills the space.
   * Requires a `fillHeight` `NavioPage` ancestor. Only use when every tab's content is meant to
   * fill height (e.g. a table); tall non-scrolling content in a tab would overflow.
   */
  fillHeight?: boolean;
  /** Labels the row renders itself. English by default; the customer app passes its Lingui strings. */
  translations?: NoddiTabsTranslations;
  /**
   * `segmented` (default) switches views inside content. `underline` is page navigation, drawn on the
   * bottom border of a page header — `NavioPage` uses it; reach for that page rather than this.
   */
  variant?: NoddiTabsVariant;
  /**
   * Places the tab row and the panels apart, e.g. the row in a page header and the panels in the page
   * body. Both parts must end up inside the returned tree, which renders within the tabs root.
   * Without it the row sits directly above the panels.
   */
  renderLayout?: (parts: { tabList: ReactNode; panels: ReactNode }) => ReactNode;
}

export type NoddiTabsVariant = 'segmented' | 'underline';

export function NoddiTabs<T extends string>(props: NoddiTabsProps<T>) {
  const {
    tabItems,
    defaultValue,
    ariaLabel,
    searchParamName = 'tab',
    value: controlledValue,
    onValueChange,
    keepExistingSearchParams = false,
    trailingNode,
    contentClassName,
    fillHeight = false,
    translations = commonTexts.tabs,
    variant = 'segmented',
    renderLayout
  } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionQueryParams = useSessionQueryParams();
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  // Filter out hidden tabs
  const filteredTabs = tabItems.filter((item) => !item.hide);

  // Determine if component is controlled
  const isControlled = 'value' in props && 'onValueChange' in props;

  // Get current tab value
  // eslint-disable-next-line no-restricted-syntax -- assigned by the controlled-value vs search-param branch
  let currentTab: T;
  if (isControlled && controlledValue !== undefined) {
    currentTab = controlledValue;
  } else {
    const tab = searchParams.get(searchParamName);
    const validTabValues = filteredTabs.map((item) => item.value);
    currentTab = validTabValues.includes(tab as T) ? (tab as T) : defaultValue;
  }

  // One node for the real trigger and its hidden twin, so the two can never measure differently.
  const overflowTriggerContent = (
    <>
      <span>{translations.more}</span>
      <NoddiIcon size='medium' name='ChevronDown' />
    </>
  );
  // Likewise: the real separator and the twin whose width the trigger's reservation includes.
  const overflowSeparator = (
    <span aria-hidden className='flex w-3 flex-none justify-center self-center'>
      <span className='h-4 w-px bg-grey/40' />
    </span>
  );
  const measuredVisibleCount = useOverflowVisibleCount({ itemCount: filteredTabs.length, rowRef, measureRef });

  const handleTabChange = (value: string) => {
    if (isControlled && onValueChange) {
      onValueChange(value as T);
    } else {
      const newParams = nextTabSearchParams(
        searchParams,
        searchParamName,
        value,
        keepExistingSearchParams,
        sessionQueryParams
      );
      setSearchParams(newParams, { replace: true });
    }
  };

  const handleTabClick = (value: string, event: React.MouseEvent) => {
    // Check if Cmd (Mac) or Ctrl (Windows) key is pressed
    if (event.metaKey || event.ctrlKey) {
      const newParams = nextTabSearchParams(
        searchParams,
        searchParamName,
        value,
        keepExistingSearchParams,
        sessionQueryParams
      );

      // Construct the full URL for the new tab
      const url = `${window.location.pathname}?${newParams.toString()}`;

      // Open in new tab
      window.open(url, '_blank');

      // Prevent default tab change
      event.preventDefault();
      return;
    }

    // Normal behavior - change tab
    handleTabChange(value);
  };

  const visibleCount = measuredVisibleCount ?? filteredTabs.length;
  const visibleTabs = filteredTabs.slice(0, visibleCount);
  const overflowTabs = filteredTabs.slice(visibleCount);
  // Which tabs the row shows never depends on the selection, so the menu carries the marker whenever
  // the selected tab is inside it.
  const selectionIsInMenu = overflowTabs.some((item) => item.value === currentTab);

  const tabList = (
    <div className='flex items-center justify-between gap-4'>
      <div ref={rowRef} className='relative min-w-0 flex-1'>
        <TabsList variant={variant} aria-label={ariaLabel} className={cn('flex-nowrap', fillHeight && 'shrink-0')}>
          {visibleTabs.map((item) => (
            <TabsTrigger
              key={item.value}
              value={item.value}
              variant={variant}
              disabled={item.disabled}
              onClick={(event) => handleTabClick(item.value, event)}
            >
              {renderTabTriggerContent(item, item.value === currentTab, variant)}
            </TabsTrigger>
          ))}
          {overflowTabs.length > 0 && (
            // Tab navigation must not lock page scrolling: mobile viewport overflow can be
            // mistaken for scrollbar width, shrinking the page when the menu opens.
            <DropdownMenu modal={false}>
              {/* The trigger is a control, not one more tab: a hairline keeps it from reading as the
                  next item in the row. Nothing to separate it from once the row is too narrow for
                  even one tab. */}
              {visibleTabs.length > 0 && overflowSeparator}
              {/* A menu button is not a tab, so it stays out of the tablist's arrow-key ring and is
                  reached with Tab instead. */}
              <DropdownMenuTrigger
                // The selected-tab styling comes from `selected` rather than the variant's
                // `data-[state=active]`: Radix owns `data-state` on a menu trigger and writes
                // open/closed into it.
                className={cn(selectionControlItemVariants({ variant, selected: selectionIsInMenu }), 'flex-none')}
              >
                {overflowTriggerContent}
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                {overflowTabs.map((item) => (
                  <DropdownMenuItem
                    key={item.value}
                    disabled={item.disabled}
                    aria-current={item.value === currentTab}
                    onClick={(event) => handleTabClick(item.value, event)}
                    className={cn(item.value === currentTab && 'bg-gray-100 font-medium')}
                  >
                    {item.icon && <NoddiIcon size='medium' name={item.icon} />}
                    <span className='flex-1'>{toSentenceCase(item.label)}</span>
                    {item.badge}
                    {!item.badge && item.count !== undefined && item.count >= 0 && (
                      <span className='text-xs text-gray-500'>{item.count}</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </TabsList>
        {/* The measurement row is wider than the row it measures by design, and `invisible` only
            stops it painting — its right edge would still stretch the page's scrollable area, so
            clip it to the row's own width here. */}
        <div aria-hidden className='pointer-events-none absolute inset-x-0 top-0 h-0 overflow-hidden'>
          {/* Every tab at its natural width, laid out but never painted. Measuring the real row
              instead would feed its own output back in — hiding a tab frees the width that pushed it
              out. `badge` renders twice as a result, so keep it a presentational node. */}
          <div
            ref={measureRef}
            className={cn(selectionControlListVariants({ variant }), 'invisible relative w-max flex-nowrap')}
          >
            {filteredTabs.map((item) => (
              <button
                key={item.value}
                type='button'
                tabIndex={-1}
                // `flex-none` where the real trigger grows: the row is measured for what each tab
                // needs, not for how the list hands out leftover space.
                className={cn(selectionControlItemVariants({ variant }), 'flex-none')}
              >
                {renderTabTriggerContent(item, false, variant)}
              </button>
            ))}
            {/* The separator and menu trigger arrive together, so they are measured as one. */}
            <span className='absolute flex w-max'>
              {overflowSeparator}
              <button
                type='button'
                tabIndex={-1}
                className={cn(selectionControlItemVariants({ variant }), 'flex-none')}
              >
                {overflowTriggerContent}
              </button>
            </span>
          </div>
        </div>
      </div>
      {trailingNode}
    </div>
  );

  const panels = filteredTabs.map((item) => (
    <TabsContent
      key={item.value}
      value={item.value}
      // `data-[state=active]:` scope is required: an unconditional `flex` (display:flex) would
      // override the `hidden` attribute Radix puts on inactive panels (author CSS beats the UA
      // `[hidden]` rule), making every tab's content stack and split the height.
      className={cn(fillHeight && 'min-h-0 data-[state=active]:flex data-[state=active]:flex-col', contentClassName)}
    >
      {item.node}
    </TabsContent>
  ));

  return (
    <Tabs
      value={currentTab}
      onValueChange={handleTabChange}
      // With a custom layout the root must not box its children: the caller's tree decides the layout,
      // and only the tabs context has to reach both parts.
      className={cn(renderLayout ? 'contents' : fillHeight && 'min-h-0 flex-1')}
    >
      {renderLayout ? (
        renderLayout({ tabList, panels })
      ) : (
        <>
          {tabList}
          {panels}
        </>
      )}
    </Tabs>
  );
}

// A plain function, not a component: it exists to keep a trigger and its hidden twin identical, and a
// component boundary here would only add props to document.
function renderTabTriggerContent(item: TabItem<string>, isActiveTab: boolean, variant: NoddiTabsVariant) {
  return (
    <div className='flex items-center gap-1.5'>
      {item.icon && <NoddiIcon size='medium' name={item.icon} />}
      <span>{toSentenceCase(item.label)}</span>
      {item.badge}
      {!item.badge && item.count !== undefined && item.count >= 0 && (
        <NoddiBadge variant='outline' className={tabCountVariants({ variant, active: isActiveTab })}>
          {item.count}
        </NoddiBadge>
      )}
    </div>
  );
}

const tabCountVariants = cva('border-none py-0', {
  variants: {
    variant: {
      segmented: 'ml-1 px-2 text-[10px]',
      underline: 'px-1.5 text-[11px]'
    },
    active: {
      true: '',
      false: ''
    }
  },
  compoundVariants: [
    { variant: 'segmented', active: true, class: 'bg-gray-200 text-gray-800' },
    { variant: 'segmented', active: false, class: 'bg-white/90' },
    { variant: 'underline', active: true, class: 'bg-primary/10 text-primary' },
    { variant: 'underline', active: false, class: 'bg-muted text-muted-foreground' }
  ]
});

function nextTabSearchParams(
  searchParams: URLSearchParams,
  searchParamName: string,
  value: string,
  keepExistingSearchParams: boolean,
  sessionQueryParams: readonly string[]
): URLSearchParams {
  const next = keepExistingSearchParams ? new URLSearchParams(searchParams) : new URLSearchParams();
  if (!keepExistingSearchParams) {
    copySessionQueryParams(searchParams, next, sessionQueryParams);
  }
  next.set(searchParamName, value);
  return next;
}
